import { BarChart3, Route, Navigation, Ruler, Car, Bus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type ToolStatus = 'available' | 'in-development' | 'planned';
export type SupportLevel = 'self-service' | 'setup-required' | 'consultation';

export interface SelfServiceTool {
  id: string;
  icon: LucideIcon;
  name: string;
  status: ToolStatus;
  supportLevel: SupportLevel;
  problem: string;
  description: string;
  tags: string[];
  image?: string;
  link: string;
}

// Retired 2026-07-01 (tool audit): map-export (superseded by MapHub styled-export),
// v500-verstedelijking (gateway page, not a real tool), arturo-sb (unused experiment —
// code archived in pages/ArturoSB.tsx, not routed), traffic-monitor (backend hung; NDW
// collector wound down, superseded by NRM/Dexter).
export const selfServiceTools: SelfServiceTool[] = [
  {
    id: 'vierkantjes',
    icon: BarChart3,
    name: 'Vierkantjes Generator',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'Need to visualize modal split data?',
    description:
      'Generate Studio Bereikbaar modal split visualizations from Excel data with automated layouts.',
    tags: ['visualization', 'transportation'],
    image: '/assets/tools/amsterdam.png',
    link: '/vierkantjes',
  },
  {
    id: 'traveltime',
    icon: Navigation,
    name: 'TravelTime Accessibility (trial)',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'Need catchments, travel-time matrices, or multimodal routes?',
    description:
      'Five analyses on the TravelTime API: isochrone, H3 catchment grid, distance map, one-to-many travel-time matrix, and multimodal route. Address search via PDOK, walking/cycling/driving/PT modes, GeoJSON export. Trial — expires 2026-10-08.',
    tags: ['accessibility', 'isochrone', 'matrix', 'route', 'h3', 'catchment', 'trial'],
    image: '/assets/tools/traveltime.png',
    link: '/traveltime',
  },
  {
    id: 'accessibility-maps',
    icon: Navigation,
    name: 'Accessibility Maps',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'Need maps showing accessibility to amenities?',
    description:
      'Generate accessibility visualization maps showing reachability to schools, hospitals, supermarkets, and more by car, bike, walking, or public transport.',
    tags: ['accessibility', 'amenities', 'mapping', 'movemobility'],
    image: '/assets/tools/accessibility-maps.png',
    link: '/accessibility-maps',
  },
  {
    id: 'dwarsdoorsnede',
    icon: Ruler,
    name: 'Dwarsdoorsnede Builder',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'Need to design a street cross-section?',
    description:
      'Compose street cross-sections at 1:100 from segments, buildings and icons. Undo/redo, save and open as JSON, share as a link, export SVG (mm) or PNG (300 dpi).',
    tags: ['design', 'street-profile', 'visualization'],
    image: '/assets/tools/dwarsdoorsnede.png',
    link: '/dwarsdoorsnede',
  },
  {
    // Gen-2 sb-nrm-toolkit (2026-08-19) — served outside this SPA at /nrm.
    id: 'traffic-viewer',
    icon: Car,
    name: 'NRM · data & export',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'NRM-netwerken, scenario\u2019s en zonedata nodig?',
    description:
      'RP2025 netwerken (5 scenario\u2019s) en SEG-zonedata in \u00e9\u00e9n kaart: verschilplot, zone-drilldown, en export als GeoPackage (QGIS-styled), GeoJSON, CSV of SVG \u2014 alles volgens de vaste SB NRM-legenda.',
    tags: ['nrm', 'rp2025', 'verschilplot', 'export', 'gen-2'],
    image: '/assets/tools/traffic-viewer.png',
    link: '/nrm',
  },
  {
    id: 'gtfs-toolkit',
    icon: Bus,
    name: 'GTFS Toolkit',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'Need transit lines, frequencies, and speeds as map layers?',
    description:
      'Pick bus/tram/metro lines from the national GTFS feed and a service day; download a 6-layer GeoPackage with line geometries, stop-to-stop hourly frequencies and speeds, dwell times, and multi-line network aggregation. Ready for QGIS/MapHub.',
    tags: ['transit', 'gtfs', 'frequencies', 'geopackage', 'ov'],
    link: '/gtfs/',
  },
  {
    id: 'spinnenkaart',
    icon: Route,
    name: 'Spinnenkaart',
    status: 'available',
    supportLevel: 'self-service',
    problem: 'Need to visualize origin-destination flows?',
    description:
      'Generate spider diagrams (desire lines) between municipalities from ODIN survey data. Filter by mode, motive, and top-N connections. Export to PNG/SVG.',
    tags: ['mobility', 'odin', 'spider', 'visualization'],
    link: '/spinnen',
  },
];

// Roadmap tiles removed from the UI 2026-07-01 (were dead '#' links). Re-add real
// entries when a tool actually ships. Kept as an empty export so consumers still import it.
export const roadmapTools: SelfServiceTool[] = [];
